import { Badge, Divider, Stack, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { isZendeskMessagingUnread } from "@src/appV2/lib/ZendeskMessaging/utils";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { TERMS_OF_SERVICE_URL } from "@src/appV2/redesign/constants/urls";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { USER_EVENTS } from "@src/constants";
import { useRef } from "react";

declare global {
  interface Window {
    adaEmbed: any;
  }
}

export function NeedHelpPage() {
  const { helpCenterWorkerSupportLink } = useSupportLinks();
  const { openArticle } = useZendeskArticle();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isEmbedded } = useEmbeddedMode();

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Get Help"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={`${RootPaths.HOME}/account`}
          headerVariant="tertiary"
        />

        <PageContent ref={scrollRef}>
          <Stack spacing={2}>
            <List
              component="div"
              sx={{
                "& li": {
                  height: 62,
                },
              }}
            >
              {!isEmbedded && (
                <>
                  <ListItem>
                    <ListItemButton
                      hideCallToActionIcon
                      onClick={async () => {
                        await openInBrowser({ url: TERMS_OF_SERVICE_URL });
                      }}
                    >
                      <ListItemIcon iconType="info" />
                      <ListItemText
                        primary="Terms of Service"
                        secondary="The contractor role & how we work together"
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </>
              )}

              <ListItem
                secondaryAction={
                  isZendeskMessagingUnread() ? (
                    <Badge color="error" variant="dot" aria-label="Unread support messages" />
                  ) : undefined
                }
              >
                <ListItemButton
                  hideCallToActionIcon
                  onClick={async () => {
                    await openZendeskMessaging();
                  }}
                >
                  <ListItemIcon iconType="chat" />
                  <ListItemText
                    primary="Contact Support"
                    secondary="Contact via chat or request a callback"
                  />
                </ListItemButton>
              </ListItem>
              <Divider />

              {!isEmbedded && (
                <>
                  <ListItem>
                    <ListItemButton
                      hideCallToActionIcon
                      onClick={async () => {
                        logEvent(USER_EVENTS.OPENED_HELP_CENTER);
                        await openArticle(helpCenterWorkerSupportLink);
                      }}
                    >
                      <ListItemIcon iconType="help-outlined" />
                      <ListItemText primary="Help Center" secondary="Guides, FAQs, and Tips" />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </>
              )}
            </List>
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
